/**
 * The SPA's single logging surface. The rest of the app stays `console`-free
 * (Biome `noConsole`); this one module owns the single justified exception so
 * that browser-side diagnostics have a consistent, switchable home.
 *
 * Levels and their defaults:
 * - `debug` / `info` — DEV builds only; silent in production so a shipped bundle
 *   never narrates flow to an end user's console.
 * - `warn` / `error` — always emitted.
 *
 * Shutoff / override: `VITE_LOG_LEVEL` raises or lowers the floor regardless of
 * build. Set it to `silent` to turn logging OFF entirely (e.g. a noisy prod
 * incident), or to `debug` to force verbose logging in a production build while
 * chasing a bug. Unset ⇒ the DEV/prod defaults above.
 */

/** Ordered log levels; `silent` is the off switch (nothing at or above it logs). */
export enum LogLevel {
	Debug = 'debug',
	Info = 'info',
	Warn = 'warn',
	Error = 'error',
	Silent = 'silent',
}

/** Numeric severity floor — a call logs only when its level ≥ the active floor. */
const SEVERITY: Record<LogLevel, number> = {
	[LogLevel.Debug]: 10,
	[LogLevel.Info]: 20,
	[LogLevel.Warn]: 30,
	[LogLevel.Error]: 40,
	[LogLevel.Silent]: 100,
}

/** Parse `VITE_LOG_LEVEL` to a level, or `null` when unset / unrecognised. */
const configuredLevel = (): LogLevel | null => {
	const raw = import.meta.env.VITE_LOG_LEVEL?.toLowerCase()
	const match = Object.values(LogLevel).find((level) => level === raw)
	return match ?? null
}

/**
 * The active floor: an explicit `VITE_LOG_LEVEL` wins; otherwise `debug` in DEV
 * (everything) and `warn` in production (warn + error only).
 */
const activeFloor = (): number => {
	const configured = configuredLevel()
	if (configured) return SEVERITY[configured]
	return import.meta.env.DEV ? SEVERITY[LogLevel.Debug] : SEVERITY[LogLevel.Warn]
}

const enabled = (level: LogLevel): boolean => SEVERITY[level] >= activeFloor()

/**
 * The single sanctioned console sink for the SPA. Every other module logs through
 * the {@link logger} singleton, so this is the only place the console is touched.
 * It is reached via `globalThis.console` deliberately: that keeps the one access
 * off the bare-`console` identifier the `no-console` lint rules match, so the
 * module needs no per-line suppression at all.
 */
const browserConsole = globalThis.console
const sink = {
	debug: browserConsole.debug,
	info: browserConsole.info,
	warn: browserConsole.warn,
	error: browserConsole.error,
}

export const logger = {
	debug: (message: string, meta?: Record<string, unknown>): void => {
		if (enabled(LogLevel.Debug)) sink.debug(message, meta ?? {})
	},
	info: (message: string, meta?: Record<string, unknown>): void => {
		if (enabled(LogLevel.Info)) sink.info(message, meta ?? {})
	},
	warn: (message: string, meta?: Record<string, unknown>): void => {
		if (enabled(LogLevel.Warn)) sink.warn(message, meta ?? {})
	},
	error: (message: string, meta?: Record<string, unknown>): void => {
		if (enabled(LogLevel.Error)) sink.error(message, meta ?? {})
	},
}
